import React, { useState } from 'react';
import { FaTrash } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { TipRecord } from '../../types';
import { cancelTipRecord, deleteTipRecord } from '../../../../../../crud/payments_api/tip_management';
import './TipRecordsList.scss';

interface TipRecordsListProps {
  records: TipRecord[];
  onCreateNew: () => void;
  onViewRecord?: (record: TipRecord) => void;
  onRecordUpdated?: () => void;
}

const TipRecordsList: React.FC<TipRecordsListProps> = ({
  records,
  onCreateNew,
  onViewRecord,
  onRecordUpdated,
}) => {
  const [deletingRecords, setDeletingRecords] = useState<Set<string>>(new Set());
  const [togglingRecords, setTogglingRecords] = useState<Set<string>>(new Set());
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-CL', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDateTime = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-CL', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleToggleStatus = async (record: TipRecord, e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (togglingRecords.has(record.id)) return;
    
    setTogglingRecords(prev => new Set(prev).add(record.id));
    
    try {
      await cancelTipRecord(record.id);
      toast.success(record.status === 'completed' ? 'Registro cancelado' : 'Registro reactivado');
      // Refresh the list
      onRecordUpdated?.();
    } catch (error) {
      console.error('Error al cambiar estado del registro:', error);
      toast.error('Error al cambiar el estado del registro');
    } finally {
      setTogglingRecords(prev => {
        const newSet = new Set(prev);
        newSet.delete(record.id);
        return newSet;
      });
    }
  };

  const handleDelete = async (record: TipRecord, e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (deletingRecords.has(record.id)) return;
    
    if (!window.confirm(`¿Estás seguro de eliminar el registro del período ${formatDate(record.start_date)} - ${formatDate(record.end_date)}?`)) {
      return;
    }
    
    setDeletingRecords(prev => new Set(prev).add(record.id));
    
    try {
      await deleteTipRecord(record.id);
      toast.success('Registro eliminado exitosamente');
      // Refresh the list
      onRecordUpdated?.();
    } catch (error) {
      console.error('Error al eliminar registro:', error);
      toast.error('Error al eliminar el registro');
    } finally {
      setDeletingRecords(prev => {
        const newSet = new Set(prev);
        newSet.delete(record.id);
        return newSet;
      });
    }
  };

  return (
    <div className="tip-records-list">
      <div className="records-header">
        <div className="header-text">
          <h2>Historial de Distribución de Propinas</h2>
          <p>Registros anteriores de distribución de propinas</p>
        </div>
        <button className="btn btn-create" onClick={onCreateNew}>
          <span className="btn-icon">+</span>
          Crear Registro
        </button>
      </div>

      {records.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📋</div>
          <h3>No hay registros</h3>
          <p>Crea tu primer registro de distribución de propinas</p>
          <button className="btn btn-create-empty" onClick={onCreateNew}>
            <span className="btn-icon">+</span>
            Crear Primer Registro
          </button>
        </div>
      ) : (
        <div className="records-grid">
          {records.map((record) => (
            <div
              key={record.id}
              className={`record-card ${record.status}`}
              onClick={() => onViewRecord?.(record)}
            >
              <div className="record-header">
                <div className="header-left">
                  <span className={`status-badge ${record.status}`}>
                    {record.status === 'completed' ? 'Completado' : 'Borrador'}
                  </span>
                  <span className="record-date">
                    Creado: {formatDateTime(record.created_at)}
                  </span>
                </div>
                <div className="header-actions">
                  <label className="switch" onClick={(e) => e.stopPropagation()}>
                    <input
                      className="toggle-switch-item-checkbox"
                      type="checkbox"
                      checked={record.status === 'completed'}
                      onChange={(e) => handleToggleStatus(record, e as any)}
                      disabled={togglingRecords.has(record.id)}
                    />
                    <span className="slider round"></span>
                  </label>
                  <button
                    className="btn-delete"
                    onClick={(e) => handleDelete(record, e)}
                    disabled={deletingRecords.has(record.id)}
                    title="Eliminar registro"
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>

              <div className="record-body">
                <div className="date-range">
                  <span className="date-label">Período:</span>
                  <span className="dates">
                    {formatDate(record.start_date)} - {formatDate(record.end_date)}
                  </span>
                </div>

                <div className="record-stats">
                  <div className="stat">
                    <span className="stat-value">{formatCurrency(record.total_tips)}</span>
                    <span className="stat-label">Total Propinas</span>
                  </div>
                  <div className="stat">
                    <span className="stat-value">{record.employee_count}</span>
                    <span className="stat-label">Empleados</span>
                  </div>
                  <div className="stat">
                    <span className="stat-value">{record.shift_ids.length}</span>
                    <span className="stat-label">Turnos</span>
                  </div>
                </div>
              </div>

              <div className="record-footer">
                <button className="btn btn-view">
                  Ver Detalle →
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TipRecordsList;
